import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import 'features/account/styles/Account.scss';
import { IBillingProfile } from 'features/checkout/types/checkout.types';
import { Box, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import { COUNTRIES } from 'config/regions.const';
import { useAppSelector } from 'app/hooks';
import { accountStore } from 'features/account/stores/account.slice';
import { convertToExpiration, formatExpirationDate } from '../utils/payment.helper';
import BrandCreditCardIcon from 'components/BrandCreditCardIcon';

interface IEditStripePaymentDialogContentProps {
   editPaymentId: string;
}

const EditStripePaymentDialogContent: React.FC<IEditStripePaymentDialogContentProps> = ({ editPaymentId }) => {
   const { billingProfiles } = useAppSelector(accountStore);
   const [accountNum, setAccountNum] = useState('');
   const [cardBrand, setCardBrand] = useState('');
   const { control } = useFormContext();

   const countryList = COUNTRIES.map((country) => {
      return {
         name: country.name,
         value: country.value,
      };
   });

   const { setValue } = useFormContext();

   useEffect(() => {
      const profileToEdit = billingProfiles.find((billingProfile) => billingProfile.id === editPaymentId);
      if (profileToEdit) {
         setAccountNum(profileToEdit.accountNum);
         profileToEdit.cardBrand && setCardBrand(profileToEdit.cardBrand);
         Object.keys(profileToEdit).forEach((key) => {
            let editValue =
               profileToEdit[key as keyof IBillingProfile] === null ? '' : profileToEdit[key as keyof IBillingProfile];

            if (key === 'expiry') {
               editValue = convertToExpiration(editValue as string);
            }
            setValue(key as keyof IBillingProfile, editValue);
         });
      }
   }, [editPaymentId, setValue, billingProfiles]);

   return (
      <Box sx={{ p: 0, mt: 1, mb: 1 }}>
         <Box display="flex" alignItems="center" mb={4} component="span">
            <BrandCreditCardIcon fontSize="1.2rem" brand={cardBrand} />
            <Typography ml={0.5} fontSize="1.2rem">
               {`Card ending in  ${accountNum}`}
            </Typography>
         </Box>

         <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
               <Controller
                  name="expiry"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                     <TextField
                        {...field}
                        name="expiry"
                        label="Expiration Date"
                        placeholder="MM/YY"
                        inputProps={{ maxLength: 5 }}
                        onChange={(e) => {
                           field.onChange(formatExpirationDate(e.target.value));
                        }}
                        error={!!error}
                        helperText={error ? error.message : ''}
                        variant="outlined"
                     />
                  )}
               />
            </Grid>
            <Grid item xs={12} sm={6}>
               <TextField disabled label="Security code" fullWidth defaultValue="•••" />
            </Grid>
            <Grid item xs={12} sm={6}>
               <FormSelect name="country" label="Country" autoComplete="billing country" menuItems={countryList} />
            </Grid>
            <Grid item xs={12} sm={6}>
               <FormInput
                  name="postal"
                  label="Zip / Postal Code"
                  autoComplete="billing postal-code"
                  inputProps={{ maxLength: 10 }}
               />
            </Grid>
         </Grid>
      </Box>
   );
};

export default EditStripePaymentDialogContent;
